import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";

// AppFrameHeal - after the contact frame has loaded, puts back any of its stylesheets that did not apply (2026-09-28).
//
// ==================================================================================================
// !! ⛔ WHY (P0, owner screenshot 2026-09-27/28: "There are some serious graphics issues to be fixed")
// ==================================================================================================
//
// `AppFrameStyle` lifts every `<style>` block and `style=` attribute of a contact page into `blob:` stylesheets
// (`AppFrameSheet`), because the app's Content-Security-Policy has no 'unsafe-inline' for styles. That makes the
// frame's WHOLE look depend on those `<link>`s loading. When they do not, the page is drawn with no CSS at all:
// the Insights and Details checkboxes bare in the top-left corner, the contact's 640px profile picture filling
// the frame, the conversation pushed out of sight - exactly the owner's screenshot. REPRODUCED in real Chrome
// against the live app and the owner's real page by running today's `app.js` under the index.html policy from
// before 2026-09-27's tightening (`style-src 'self' 'unsafe-inline'`, no `blob:`): every lifted sheet is
// refused, and the frame looks exactly like that. ⚠ The owner's Edge brings the WACommander tab back with a
// RELOAD-type navigation (their history: 2026-09-27 14:18Z and 2026-09-28 00:44Z) - a restored tab can take the
// document from the cache while `app.js` comes fresh, so the page runs one build's code under ANOTHER build's
// policy. A revoked `blob:` URL, or a browser that refuses `blob:` sheets, ends the same way.
//
// ⭐ THE HEAL: on the frame's `load` (which fires only after every stylesheet has loaded or failed), each of OUR
// sheet links is checked - no `sheet` (a failed or revoked load) or `cssRules` that cannot be read (a sheet the
// policy refused) counts as missing. If ANY is missing, ALL the page's lifted sheets are installed again, in their
// order, as CONSTRUCTED stylesheets (`new CSSStyleSheet()` of the FRAME's window + `replaceSync` +
// `adoptedStyleSheets`). ⭐ CSSOM is not governed by `style-src` - measured in Chrome 153 under the live strict
// policy AND under the old one: rules applied, zero CSP messages. All of them, not only the missing one, so the
// cascade order the page was written for (page sheet, then the app's frame CSS and the attribute rules) holds.
// ⚠ The marker `data-wac-healed="<n>"` on the frame's `<html>` says it happened (the e2e reads it); a browser with
// no constructed stylesheets is marked `data-wac-healed="unsupported"` and left as it is (fate 1 - see `Install`).
// ⚠ SHAPED BY INTERFACES, so the suite drives it with a fake frame (Node has no DOM).

/** A stylesheet, as far as this reads one. ⚠ Reading `cssRules` of a sheet the policy refused THROWS. */
export interface IHealSheet {
    readonly cssRules: ArrayLike<unknown>;
}

/** A `<link>` element. */
export interface IHealLink {
    getAttribute(Name: string): string | null;
    readonly sheet: IHealSheet | null;
}

/** A constructed stylesheet. */
export interface IHealConstructed {
    replaceSync(Text: string): void;
}

/** The frame's `<html>`. */
export interface IHealRoot {
    setAttribute(Name: string, Value: string): void;
}

/** The frame's document. `adoptedStyleSheets` is absent where constructed sheets are not supported. */
export interface IHealDoc {
    querySelectorAll(Selector: string): ArrayLike<IHealLink>;
    adoptedStyleSheets?: IHealConstructed[];
    readonly documentElement: IHealRoot | null;
}

/** The frame's window: its OWN `CSSStyleSheet` (a sheet made in another document cannot be adopted). */
export interface IHealWindow {
    CSSStyleSheet?: new () => IHealConstructed;
}

/** The frame, as far as this is concerned. An `HTMLIFrameElement` satisfies it by shape. */
export interface IHealFrame {
    readonly contentDocument: IHealDoc | null;
    readonly contentWindow: IHealWindow | null;
    addEventListener(Type: string, Listener: () => void): void;
}

export class AppFrameHealRules {
    /** The attribute that says a heal happened, and how many sheets were installed. */
    public static readonly HealedAttr: string = "data-wac-healed";
    /** `HealedAttr`'s value when the sheets were missing and this browser cannot install them (no constructed sheets). */
    public static readonly Unsupported: string = "unsupported";

    /**
     * Did this sheet apply? DOCUMENTED EXCEPTION: a probe. ERROR-SUPPRESSED-JUSTIFIED: reading `cssRules` of a
     * sheet the Content-Security-Policy refused throws a SecurityError - that throw IS the answer "not applied".
     */
    public static Applied = (Link: IHealLink | null): boolean => {
        try {
            if (Link === null || Link.sheet === null || Link.sheet === undefined) { return false; }
            return typeof Link.sheet.cssRules.length === "number";
        } catch (e) {
            return false;
        }
    };

    /** True when any of OUR sheets (by href) is not linked in the document or did not apply. */
    public static Missing = (Links: ArrayLike<IHealLink>, Hrefs: string[]): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            for (const Href of Hrefs) {
                if (nz(Href).length === 0) { continue; }
                let Found: IHealLink | null = null;
                for (let At: number = 0; At < Links.length; At = At + 1) {
                    if (nz(Links[At].getAttribute("href")) === Href) { Found = Links[At]; break; }
                }
                if (AppFrameHealRules.Applied(Found) === false) { return true; }
            }
            return false;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppFrameHealRules.Missing");
            return false;
        }
    };
}

export class AppFrameHeal {
    /** The page's lifted sheets, in order, and the `blob:` hrefs they were linked with (same order). */
    private Texts: string[];
    private Hrefs: string[];
    private Frame: IHealFrame | null = null;

    public constructor(Texts: string[], Hrefs: string[]) {
        this.Texts = Texts.slice();
        this.Hrefs = Hrefs.slice();
    }

    /** Check (and heal) every time the frame loads - a reload or a history step loads the sheets again. */
    public Attach = (Frame: IHealFrame): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Frame = Frame;
            Frame.addEventListener("load", this.OnLoad);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppFrameHeal.Attach");
        }
    };

    public OnLoad = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Frame === null || this.Frame.contentDocument === null) { return; }
            this.Check(this.Frame.contentDocument, this.Frame.contentWindow);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppFrameHeal.OnLoad");
        }
    };

    /** Answers how many sheets were installed (0 = nothing was missing). */
    public Check = (Doc: IHealDoc, Win: IHealWindow | null): number => {
        if (getCoreUtils().KErrorMode === true) { return 0; }
        try {
            if (this.Texts.length === 0) { return 0; }
            if (AppFrameHealRules.Missing(Doc.querySelectorAll("link[rel=stylesheet]"), this.Hrefs) === false) { return 0; }
            return this.Install(Doc, Win);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppFrameHeal.Check");
            return 0;
        }
    };

    /** Every lifted sheet again, as constructed sheets of the frame's own window, after any already adopted. */
    private Install = (Doc: IHealDoc, Win: IHealWindow | null): number => {
        if (getCoreUtils().KErrorMode === true) { return 0; }
        try {
            if (Win === null || typeof Win.CSSStyleSheet !== "function" || Array.isArray(Doc.adoptedStyleSheets) === false) {
                // ⛔ FATE 1, NOT A REPORT (2026-09-28): an older browser (Safari < 16.4) without constructed stylesheets is
                // the visitor's browser, not our defect - and since the app's handler LATCHES, a report here would turn
                // every button of the visit off over one unstyled page. Marked, so the e2e can still see it happened.
                if (Doc.documentElement !== null) { Doc.documentElement.setAttribute(AppFrameHealRules.HealedAttr, AppFrameHealRules.Unsupported); }
                return 0;
            }
            const Made: IHealConstructed[] = [];
            for (const Text of this.Texts) {
                const One: IHealConstructed = new (Win.CSSStyleSheet as new () => IHealConstructed)();
                One.replaceSync(Text);
                Made.push(One);
            }
            Doc.adoptedStyleSheets = (Doc.adoptedStyleSheets as IHealConstructed[]).concat(Made);
            if (Doc.documentElement !== null) { Doc.documentElement.setAttribute(AppFrameHealRules.HealedAttr, String(Made.length)); }
            return Made.length;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppFrameHeal.Install");
            return 0;
        }
    };
}
